import { fetcherConfig } from '../../lib/api-fetcher';
import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { usePageTitle } from '../../lib/hooks';
import { defineFormPage, useFormPage } from '@repo/form';
import { z } from 'zod';
import { Lock, ArrowLeft } from 'lucide-react';

import { FormField, Input } from '../../components/ui/FormField';
import { Button } from '../../components/ui/Button';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Stack } from '../../components/layout/Stack';
import { createAuthResource } from '@repo/api-client';
import { useToast } from '../../components/ui/Toast';

const authResource = createAuthResource(fetcherConfig);

const resetPasswordFormConfig = defineFormPage({
  resource: 'auth-reset-password',
  defaultValues: { password: '', confirmPassword: '' },
  schema: z.object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  }),
});

export const Route = createFileRoute('/_public/reset-password')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    return {
      token: typeof search.token === 'string' ? search.token : undefined,
    };
  },
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  usePageTitle('Reset Password');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { toast } = useToast();
  const [submitError, setSubmitError] = useState<string | undefined>();

  const token = search.token;

  const { form, isSubmitting, fieldError: fe } = useFormPage(
    resetPasswordFormConfig,
    { mutateAsync: async (v) => v },
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.setErrorMap({ onSubmit: undefined });
    setSubmitError(undefined);
    await form.handleSubmit();
    if (!form.state.canSubmit) return;
    if (!token) {
      setSubmitError('Invalid or missing reset token');
      return;
    }
    try {
      await authResource.confirmPasswordReset({ token, newPassword: form.getFieldValue('password') });
      toast({
        type: 'success',
        title: 'Success',
        description: 'Your password has been reset successfully. Please log in.',
      });
      void navigate({ to: '/login' });
    } catch (err: unknown) {
      console.error('Failed to reset password:', err);
      let msg = 'Failed to reset password';
      if (err instanceof Error) {
        msg = err.message;
      } else if (typeof err === 'object' && err !== null) {
        const obj = err as Record<string, unknown>;
        if ('message' in obj && typeof obj.message === 'string') {
          msg = obj.message;
        } else if ('error' in obj && typeof obj.error === 'string') {
          msg = obj.error;
        }
      }
      setSubmitError(msg);
    }
  };

  if (!token) {
    return (
      <AuthLayout title="Invalid Request">
        <Stack gap="6" className="items-center text-center">
          <p className="text-sm text-surface-600">
            The password reset link is invalid or has expired. Please request a new one.
          </p>
          <Button
            type="button"
            variant="primary"
            onClick={() => void navigate({ to: '/forgot-password' })}
            className="w-full mt-4"
          >
            Request new link
          </Button>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Set New Password">
      <div className="mb-6">
        <p className="text-sm text-surface-600">
          Please enter your new password below.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Stack gap="4">
          <form.Field
            name="password"
            validators={{
              onChange: ({ value }: { value: any }) => {
                const res = z.string().min(8, 'Password must be at least 8 characters').safeParse(value);
                return res.success ? undefined : res.error.issues[0].message;
              }
            }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <FormField label="New Password" required error={error} htmlFor="password">
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={!!error}
                    disabled={isSubmitting}
                    placeholder="••••••••"
                    startIcon={<Lock className="w-5 h-5" />}
                  />
                </FormField>
              );
            }}
          </form.Field>

          <form.Field
            name="confirmPassword"
            validators={{
              onChangeListenTo: ['password'],
              onChange: ({ value, fieldApi }: { value: any; fieldApi: any }) => {
                if (value !== fieldApi.form.getFieldValue('password')) {
                  return 'Passwords do not match';
                }
                return undefined;
              }
            }}
          >
            {(field: any) => {
              const error = fe(field.state.meta);
              return (
                <FormField label="Confirm Password" required error={error} htmlFor="confirmPassword">
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={!!error}
                    disabled={isSubmitting}
                    placeholder="••••••••"
                    startIcon={<Lock className="w-5 h-5" />}
                  />
                </FormField>
              );
            }}
          </form.Field>

          {submitError && (
            <p className="text-xs text-danger-text text-center" role="alert">
              {submitError}
            </p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={isSubmitting}
            className="w-full"
          >
            Reset password
          </Button>

          <div className="flex justify-center mt-4">
            <Link to="/login" className="text-sm text-surface-500 hover:text-surface-700 transition-colors flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </Stack>
      </form>
    </AuthLayout>
  );
}
